import { useState, useMemo, useCallback, useEffect } from 'react';
import { mySubordinatesApi, SUBORDINATE_DEPT_OPTIONS } from '../api/mySubordinatesApi';
import { appToast } from '../../../../components/AppToast';

export function useMySubordinatesList() {
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'All', label: 'All Subordinates' },
    { value: 'Active', label: 'Active / On Duty' },
    { value: 'On Leave', label: 'On Leave' },
  ];

  const searchFieldOptions = [
    { value: 'Name', label: 'Name' },
    { value: 'Designation', label: 'Designation' },
    { value: 'Email', label: 'Email ID' },
    { value: 'Mobile', label: 'Mobile No' },
  ];

  const tools = [
    { key: 'pdf', label: 'Export Subordinates Report to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Subordinates to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Subordinates Roster', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await mySubordinatesApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Department filter
      if (departmentFilter !== 'All' && item.department !== departmentFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Name' && !item.name?.toLowerCase().includes(query)) return false;
        if (searchField === 'Designation' && !item.designation?.toLowerCase().includes(query)) return false;
        if (searchField === 'Email' && !item.email?.toLowerCase().includes(query)) return false;
        if (searchField === 'Mobile' && !item.mobile?.includes(query)) return false;
      }

      return true;
    });
  }, [records, departmentFilter, statusFilter, searchText, searchField]);

  // Summary statistics
  const summary = useMemo(() => {
    const totalSubordinates = filteredRecords.length;
    const activeCount = filteredRecords.filter((r) => r.status === 'Active').length;
    let pendingRequestsTotal = 0;
    const departments = new Set(filteredRecords.map((r) => r.department).filter(Boolean));

    filteredRecords.forEach((r) => {
      pendingRequestsTotal += Number(r.pendingRequests || 0);
    });

    return {
      totalSubordinates,
      activeCount,
      departmentCount: departments.size,
      pendingRequestsTotal,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Subordinates filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setDepartmentFilter('All');
    setStatusFilter('All');
    setSearchText('');
    setSearchField('Name');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Subordinates to PDF...');
    } else if (key.includes('excel')) {
      appToast.success('Exporting Subordinates to Excel...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      departmentFilter,
      statusFilter,
      searchField,
      searchText,
      isLoading,
      departmentOptions: SUBORDINATE_DEPT_OPTIONS,
      statusOptions,
      searchFieldOptions,
      tools,
      summary,
    },
    actions: {
      setDepartmentFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
    },
  };
}
